import React from 'react'
import { useFormik } from 'formik'
import { FormattedMessage } from 'react-intl'
import { Button, Typography, AccordionSummary, AccordionDetails, Accordion } from '@material-ui/core'
import DateFnsUtils from '@date-io/date-fns'
import arSA from 'date-fns/locale/ar-SA'
import enUS from 'date-fns/locale/en-US'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { packageCreateRequestFormValidation } from '../../utils/validationSchema'
import { FormLoader } from '../../components/Loader/FormLoader'
import { formStyles } from '../../components/Invoice/Steps/FormStyles'
import FormSelectField from '../FormSelectField'
import FormTextField from '../FormTextField'
import FormDateField from '../FormDateField'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import LabelImportantIcon from '@material-ui/icons/LabelImportant'
import packageFormats from '../../configurations/package-format'
import packageTypes from '../../configurations/package-types'
import invoiceStatus from '../../configurations/invoices-status'
import invoiceTypes from '../../configurations/invoices-types'
import moment from 'moment'
import receiverTypes from '../../configurations/receiver-types'
import { AuthConsumer } from '../../context/AuthContext'
import FormTagsField from '../FormTagsField'
import FormListAddMore from '../FormListAddMore'


const InvoicePackageForm = ({ onSubmit, locale, cancelDialog, currentAction }) => {
  const classes = formStyles()
  const [dateFrom, setDateFrom] = React.useState(null)
  const [dateTo, setDateTo] = React.useState(moment())

  const formActions = useFormik({
    initialValues: {
      type: '',
      format: '',
      representedTaxpayerFilterType: '1',

      dateFrom: null,
      dateTo: null,

      documentTypeNames: [],
      statuses: [],
      receiverSenderType: '',
      receiverSenderId: '',
      branchNumber: '',
      productsInternalCodes: [],
      itemCodes: []
    },
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: packageCreateRequestFormValidation,
    onSubmit: ((values, formikHelpers) => {
      onSubmit(values, formikHelpers)
    })
  })

  React.useEffect(() => {
      handleDateChange(new Date(), 'dateTo', setDateTo)
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const handleDateChange = (date, fieldName, setDate) => {
    setDate(date)
    formActions.setFieldValue(fieldName, date || '')
  }
  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale === 'ar' ? arSA : enUS}>
      <form onSubmit={formActions.handleSubmit} noValidate autoComplete="off" className={classes.formWrapper}>

        <Accordion defaultExpanded={true} className={classes.FormRow}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography className={classes.RequestHeading}><LabelImportantIcon/><FormattedMessage
              id='InvoicePackage.Form.details.title'/></Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.formWrapper}>
              <FormSelectField name={'type'} labelKey={'Fields.label.view.Package.type'} list={packageTypes}
                               displayEmpty locale={locale} classes={classes} formActions={formActions} required/>
              <FormSelectField name={'format'} labelKey={'Fields.label.view.Package.format'} displayEmpty
                               list={formActions.values.type === 'Summary' ? packageFormats : packageFormats.filter(item => item.key !== 'CSV')}
                               classes={classes} formActions={formActions} required/>
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded={true} className={classes.FormRow}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography className={classes.RequestHeading}><LabelImportantIcon/><FormattedMessage
              id='InvoicePackage.Form.query.title'/></Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.formWrapper}>
              <FormDateField name={'dateFrom'} labelKey={'Fields.label.view.Package.dateFrom'}
                             classes={classes} formActions={formActions} disableFuture required value={dateFrom}
                             handleChange={(date) => handleDateChange(date, 'dateFrom', setDateFrom)}/>
              <FormDateField name={'dateTo'} labelKey={'Fields.label.view.Package.dateTo'}
                             classes={classes} formActions={formActions} disableFuture required value={dateTo}
                             atts={{
                               minDate: formActions?.values?.dateFrom ? new Date(formActions?.values?.dateFrom) : null
                             }}
                             handleChange={(date) => handleDateChange(date, 'dateTo', setDateTo)}/>


              <FormSelectField name={'documentTypeNames'} labelKey={'Fields.label.view.Package.documentTypeNames'}
                               list={invoiceTypes} locale={locale} classes={classes} formActions={formActions}
                               multiple displayEmpty/>
              <FormSelectField name={'statuses'} labelKey={'Fields.label.view.Package.statuses'}
                               list={invoiceStatus.filter(status => status.key !== 'Submitted')}
                               locale={locale} classes={classes} formActions={formActions} multiple displayEmpty/>


              <FormSelectField name={'receiverSenderType'} labelKey={'Fields.label.view.Package.receiverSenderType'}
                               classes={classes} locale={locale} formActions={formActions} list={receiverTypes}
                               displayEmpty/>

              <FormTextField name={'receiverSenderId'} disabled={!formActions.values.receiverSenderType}
                             labelKey={`Fields.label.view.Package.receiverSenderId.${formActions.values.receiverSenderType}`}
                             classes={classes} formActions={formActions}/>


              <AuthConsumer>
                {({ taxpayerData }) => (
                  <FormSelectField name={'branchNumber'} labelKey={'Fields.label.view.Package.branchNumber'}
                                   classes={classes} row formActions={formActions} displayEmpty
                                   list={taxpayerData?.branches?.map((branch, index) => ({
                                     ...branch,
                                     key: branch?.branch_number
                                   }))}/>
                )}
              </AuthConsumer>

              <FormTagsField name={'productsInternalCodes'} classes={classes} formActions={formActions}
                             labelKey={'Fields.label.view.Package.productsInternalCodes'} row/>

              <FormListAddMore labelKey={'Fields.label.view.Package.itemCodes'} entityName={'packageItemCodes'}
                               classes={classes} locale={locale} list={[]} inline noEdit
                               updateParentEntity={(value) => {
                                 console.log(value)
                                 formActions.setFieldValue('itemCodes', value)
                               }}
              />


            </div>
          </AccordionDetails>
        </Accordion>

        <div className={classes.FormSubmitRow}>
          <Button type="submit" variant='contained' size={'large'} color={'secondary'}
                  onClick={formActions.handleSubmit}
                  disabled={formActions.isSubmitting || !formActions.isValid}
          >
            <FormattedMessage id={`AddNewDialog.AddNewRequest.${currentAction}`}/>
          </Button>
          <Button variant='outlined' color='secondary'
                  onClick={cancelDialog} className={'cancel'}>
            <FormattedMessage id='invoiceSteps.Cancel'/>
          </Button>
        </div>
        {!!formActions.isSubmitting && <FormLoader loading={true}/>}
      </form>
    </MuiPickersUtilsProvider>
  )
}

export default InvoicePackageForm
